import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, from, firstValueFrom } from "rxjs";
import { shareReplay } from "rxjs/operators";
import { HybridStorageKeys, HybridStorageService } from "../../services/hybrid-storage.service";
import { SchoolWebsiteService } from "./school-website.service";
import { RuntimeConfigService } from "../../../services/runtime-config.service";
import { SchoolResource } from "../../models/school-resource.model";

/** Shape of the CMS raw-content response for ?properties=HasBeforeAndAfterSchoolCare. */
interface BeforeAfterCareRawContent {
  HasBeforeAndAfterSchoolCare?: {
    Type: string;
    Value: boolean | null;
    FileUrl: string;
  };
}

/**
 * Resolves the per-school resource links the Scholar parent portal shows, from the same
 * public nhaschools.com CMS endpoints as the lunch-menu and school-website lookups.
 */
@Injectable({
  providedIn: 'root'
})
export class SchoolResourcesService {
  // Content-editor cadence — cache for hours, not seconds.
  private static readonly CACHE_MINUTES = 6 * 60;

  // One shared lookup per school per session; persisted lookups served from hybrid storage.
  private careUrlBySchoolEID = new Map<number, Observable<string | null>>();

  private apiUrl: string;

  constructor(
    private http: HttpClient,
    private hybridStorageService: HybridStorageService,
    private schoolWebsiteService: SchoolWebsiteService,
    private runtimeConfig: RuntimeConfigService
  ) {
    this.apiUrl = this.runtimeConfig.currentConfig.apiUrl;
    this.runtimeConfig.config$.subscribe(config => {
      this.apiUrl = config.apiUrl;
    });
  }

  /** One corporate "moving out of the area" page for all schools — no per-school logic. */
  get movingUrl(): string {
    return `${this.runtimeConfig.currentConfig.cmsBaseUrl}/en/moving`;
  }

  private get cmsRawContentUrl(): string {
    return `${this.runtimeConfig.currentConfig.cmsBaseUrl}/api/schools/raw-content`;
  }

  /**
   * The school's authored resource list (links + documents) from the NHA CMS, proxied and
   * cached by our server per (school, grade). Resources are scoped per school + grade level —
   * omit the grade only to get the school's full superset. schoolEID is the NHA/SIS school ID.
   */
  getResources(schoolEID: number, gradeLevelID?: number): Observable<SchoolResource[]> {
    const url = gradeLevelID != null
      ? `${this.apiUrl}/resources/${schoolEID}/${gradeLevelID}`
      : `${this.apiUrl}/resources/${schoolEID}`;
    return this.http.get<SchoolResource[]>(url);
  }

  /**
   * Emits the school's Before & After School Care page URL, or null when the school doesn't
   * offer it (CMS flag not exactly true), the school is unknown, or the lookup fails —
   * callers hide the link either way. schoolEID is the NHA/SIS integer school ID.
   */
  getBeforeAfterCareUrl(schoolEID: number): Observable<string | null> {
    let careUrl$ = this.careUrlBySchoolEID.get(schoolEID);
    if (!careUrl$) {
      careUrl$ = from(this.loadBeforeAfterCareUrl(schoolEID)).pipe(shareReplay(1));
      this.careUrlBySchoolEID.set(schoolEID, careUrl$);
    }
    return careUrl$;
  }

  private async loadBeforeAfterCareUrl(schoolEID: number): Promise<string | null> {
    if (!await this.loadHasCareFlag(schoolEID)) {
      return null;
    }

    // The care page hangs off the school's website URL (…/schools/{NodeAlias}/en), so the
    // slug comes from the CMS alias map — never derived from the school's display name.
    const websiteUrl = await firstValueFrom(this.schoolWebsiteService.getSchoolWebsiteUrl(schoolEID));
    return websiteUrl ? websiteUrl + '/before-and-after-school-care' : null;
  }

  private async loadHasCareFlag(schoolEID: number): Promise<boolean> {
    const cacheKey = HybridStorageKeys.SchoolCare_HasCareFlagPrefix + schoolEID;

    const stored = await this.hybridStorageService.getItemIfFresh<{ hasCare: boolean }>(
      cacheKey, SchoolResourcesService.CACHE_MINUTES);
    if (stored) {
      return stored.hasCare;
    }

    try {
      // Unknown school IDs return 204 No Content (HttpClient yields a null body).
      const response = await firstValueFrom(this.http.get<BeforeAfterCareRawContent | null>(
        `${this.cmsRawContentUrl}/${schoolEID}?properties=HasBeforeAndAfterSchoolCare`));
      const hasCare = response?.HasBeforeAndAfterSchoolCare?.Value === true;

      await this.hybridStorageService.setItem(cacheKey, { hasCare });
      return hasCare;
    } catch (error) {
      // Fail soft: hide the link. Not cached, and the in-session share is dropped, so the
      // next open retries.
      console.error('Error fetching before/after care flag for school', schoolEID, error);
      this.careUrlBySchoolEID.delete(schoolEID);
      return false;
    }
  }
}
